import { type LoaderFunctionArgs, type MetaFunction, json } from '@remix-run/node'
import { Link, useLoaderData } from '@remix-run/react'
import React from 'react'
import { Button } from '#app/components/ui/button'
import { sendEmail } from '#app/utils/email.server'
import { stripe } from '#app/utils/stripe/stripe.server'

export const meta: MetaFunction = () => [{ title: 'Order Successful | Oui Chef' }]

type LoaderData = 
  | { success: true; sessionId: string; message: string; emailSent: boolean }
  | { success: false; message: string; sessionId?: undefined; emailSent?: boolean }

export const loader = async ({ request }: LoaderFunctionArgs) => {
  const url = new URL(request.url)
  const sessionId = url.searchParams.get('session_id')
  
  if (!sessionId) {
    return json<LoaderData>({ 
      success: false,
      message: 'No session ID found. Please contact support if you believe this is an error.'
    })
  }
  
  try {
    // Retrieve the session from Stripe to get the customer email
    const session = await stripe.checkout.sessions.retrieve(sessionId)
    const customerEmail = session.customer_email

    if (customerEmail) {
      // Send confirmation email
      const emailResult = await sendEmail({
        to: customerEmail,
        subject: 'Your Oui Chef Subscription is Confirmed!',
        react: (
          <SubscriptionEmail 
            sessionId={sessionId}
          />
        )
      })

      return json<LoaderData>({ 
        success: true,
        sessionId,
        message: 'Your payment was processed successfully!',
        emailSent: emailResult.status === 'success'
      })
    }

    return json<LoaderData>({ 
      success: true,
      sessionId,
      message: 'Your payment was processed successfully!',
      emailSent: false
    })
  } catch (error) {
    console.error('Error retrieving Stripe session or sending email:', error)
    
    return json<LoaderData>({ 
      success: true, // Still show success page to the user
      sessionId,
      message: 'Your payment was processed successfully!',
      emailSent: false
    })
  }
}

function SubscriptionEmail({ sessionId }: { sessionId: string }) {
  return (
    <div style={{ fontFamily: 'Arial, sans-serif', maxWidth: '600px', margin: '0 auto' }}>
      <h1 style={{ color: '#4f46e5', textAlign: 'center', margin: '20px 0' }}>
        Your Oui Chef Subscription is Confirmed!
      </h1>
      
      <div style={{ padding: '20px', border: '1px solid #e5e7eb', borderRadius: '8px', backgroundColor: '#f9fafb' }}>
        <p style={{ fontSize: '16px', color: '#374151', lineHeight: '1.5' }}>
          Thank you for subscribing to Oui Chef! Your payment has been successfully processed.
        </p>
        
        <p style={{ fontSize: '16px', color: '#374151', lineHeight: '1.5' }}>
          Order Reference: <strong>{sessionId}</strong>
        </p>
        
        <p style={{ fontSize: '16px', color: '#374151', lineHeight: '1.5', marginTop: '20px' }}>
          You now have full access to all Oui Chef features. Start optimizing your restaurant operations today!
        </p>
        
        <div style={{ margin: '30px 0', textAlign: 'center' }}>
          <a 
            href={`${process.env.BASE_URL || 'https://oui-resturants.com'}/recipes`}
            style={{
              backgroundColor: '#4f46e5',
              color: 'white',
              padding: '12px 24px',
              borderRadius: '6px',
              textDecoration: 'none',
              fontWeight: 'bold'
            }}
          >
            Go to Dashboard
          </a>
        </div>
        
        <p style={{ fontSize: '14px', color: '#6b7280', marginTop: '30px' }}>
          If you have any questions or need assistance, please don't hesitate to contact our support team at sean@ouiwiwe.com.
        </p>
      </div>
      
      <div style={{ textAlign: 'center', margin: '20px 0', color: '#6b7280', fontSize: '14px' }}>
        © {new Date().getFullYear()} Oui Chef. All rights reserved.
      </div>
    </div>
  )
}

export default function OrderSuccess() {
  const data = useLoaderData<typeof loader>()
  
  return (
    <div className="bg-white">
      <div className="mx-auto max-w-3xl px-6 py-24 sm:py-32 lg:px-8">
        <div className="text-center">
          {data.success ? (
            <>
              <div className="mx-auto h-24 w-24 flex items-center justify-center rounded-full bg-green-100 mb-8">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-12 h-12 text-green-600">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
                </svg>
              </div>
              
              <h1 className="text-4xl font-bold tracking-tight text-gray-900">Payment Successful!</h1>
              <p className="mt-4 text-lg text-gray-600">
                Thank you for your order. Your payment has been processed successfully.
              </p>
              
              <div className="mt-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
                <p className="text-sm text-gray-600">
                  Order Reference: <span className="font-medium">{data.sessionId}</span>
                </p>
                <p className="text-sm text-gray-600 mt-1">
                  Please save this reference number for your records.
                </p>
              </div>
              
              <p className="mt-8 text-lg text-gray-600">
                {data.emailSent 
                  ? "We've sent you a confirmation email with your subscription details."
                  : "You will receive a confirmation email shortly with details of your order."}
              </p>

              <div className="mt-10 flex items-center justify-center gap-6">
                <Button asChild>
                  <Link to="/recipes">Go to Dashboard</Link>
                </Button>
                
                <Button variant="outline" asChild>
                  <Link to="/">Return to Home</Link>
                </Button>
              </div>
            </>
          ) : (
            <>
              <div className="mx-auto h-24 w-24 flex items-center justify-center rounded-full bg-yellow-100 mb-8">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-12 h-12 text-yellow-600">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" />
                </svg>
              </div>
              
              <h1 className="text-3xl font-bold tracking-tight text-gray-900">Session Information Missing</h1>
              <p className="mt-4 text-lg text-gray-600">
                {data.message}
              </p>
              
              <div className="mt-10 flex items-center justify-center gap-6">
                <Button asChild>
                  <Link to="/contact">Contact Support</Link>
                </Button>
                
                <Button variant="outline" asChild>
                  <Link to="/">Return to Home</Link>
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
} 